// The Edit sheet of a New purchases row: everything that was on the row before, as compact rows.
// Category and item are tappable rows that open their chips. Tip, Split and Paid from savings are
// switch rows: Tip on shows 15 / 18 / 20%, a tip or the final total (on from the start for a
// merchant marked "Usually tipped"); Split on shows the percent picker and the note; Paid from
// savings on shows the funds (Confirm waits until one is chosen). The tip sits above the split,
// which shares the tipped total. A remembered fund is only offered, never switched on by itself.
// An auto-logged charge (source recurring) also has its amount, as the edit sheet's field.
// Nothing here saves by itself: the choices stay on the row (main.ts ui) until Confirm, here or
// on the row. Never a tip, split or fund that the user did not pick.
import type { OverviewDto, TransactionDto } from '../../../shared/api';
import { esc, money } from '../format';
import { categoryItems, spendingCategories } from './items';
import { CHOOSE_FUND, canEditAmount, deleteButton, confirmLabel, fundMissing, fundOn, fundPicked, rowCategory, rowCharge, rowNote, tipOn, type RowChoices } from './newcard';
import { amountField } from './sheet';
import { noteField } from './notes';
import { canSplitNew, newSplitBlock, sheetAmount } from './splits';
import { canTip, newTipBlock, rowTotal, tipProminent, type NewTip } from './tips';
import { canFund, funds } from './savings';
import { FUND_LABEL, fundAgain, fundPaidLine } from '../../../shared/savings';
import { TIP_ADD_PROMPT } from '../../../shared/tips';

/** The tappable rows (a pick from a list). Tip, split and paid from savings are switches. */
export type NewSheetRow = 'category' | 'item';

export interface NewSheetState {
  id: string;
  /** The rows open now. */
  open: Set<NewSheetRow>;
}

/**
 * The sheet as it opens: category and item closed. (The Tip switch starts on for a merchant marked
 * "Usually tipped": main.ts openNewSheet() sets its state; a fund is never switched on by itself.)
 */
export const newSheetState = (t: TransactionDto): NewSheetState => ({ id: t.id, open: new Set<NewSheetRow>() });

/**
 * The Tip switch, flipped: on, the tip fields with no tip yet; off, no tip (one typed in the sheet
 * is dropped, and a tip the purchase had is taken off on Confirm). "Usually tipped" is kept.
 */
export const tipSwitched = (t: TransactionDto, prev: NewTip | undefined, on: boolean): NewTip => ({
  tipCents: null,
  tipped: prev?.tipped ?? t.usuallyTipped,
  ...(on ? {} : { off: true }),
});

/** The "Paid from savings" switch, flipped: on with no fund chosen yet (Confirm waits), or off and cleared. */
export const fundSwitched = (on: boolean) => ({ fundOn: on, fund: '' });

/** The Tip state a sheet opens with: on (no tip picked) for a merchant marked "Usually tipped". */
export const startTip = (d: OverviewDto, t: TransactionDto): NewTip | undefined =>
  t.usuallyTipped && canTip(d, t) ? { tipCents: t.tipCents, tipped: true } : undefined;

/** A switch row: the label and the switch; on, what it reveals. Same as the split's. */
function switchRow(id: string, label: string, attr: string, on: boolean, body: string, after = ''): string {
  return `<div class="nopt"><label class="noptrow nswitch"><span>${esc(label)}</span><input type="checkbox" role="switch" class="switch" ${attr}="${esc(id)}"${on ? ' checked' : ''}></label>${after}${on ? `<div class="noptbody">${body}</div>` : ''}</div>`;
}

/** One compact row: the label, the value now, and (open) what changes it. */
function optRow(id: string, row: NewSheetRow, label: string, value: string, open: boolean, body: string): string {
  return `<div class="nopt"><button class="noptrow" data-a="new-row" data-id="${esc(id)}" data-row="${row}" aria-expanded="${open}"><span>${esc(label)}</span><span class="nval">${esc(value)}</span></button>${open ? `<div class="noptbody">${body}</div>` : ''}</div>`;
}

/** The Edit sheet for one New purchases row. `action` is the row's own Confirm. */
export function vNewSheet(d: OverviewDto, t0: TransactionDto, c: RowChoices, state: NewSheetState, action: 'new-confirm' | 'new-ok'): string {
  // The tip and split read the amount typed for an auto-logged charge (the bank charge they add to).
  const t = { ...t0, amountCents: rowCharge(t0, c) };
  const id = t.id;
  const cat = rowCategory(t, c);
  const catName = d.categories.find((k) => k.id === cat)?.name ?? 'Pick one';
  const rows: string[] = [];
  rows.push(
    optRow(
      id,
      'category',
      'Category',
      catName,
      state.open.has('category'),
      `<div class="chips">${spendingCategories(d)
        .map((k) => {
          const suggested = k.id === t.suggestedCategoryId && !t.categoryId;
          return `<button class="chip${suggested ? ' suggested' : ''}" data-a="new-pick" data-id="${esc(id)}" data-cat="${esc(k.id)}" aria-pressed="${k.id === cat}">${esc(k.name)}${suggested ? ' <small>Suggested</small>' : ''}</button>`;
        })
        .join('')}</div>`,
    ),
  );
  // An item, only for a category planned as a list (expenses only; transfers are marked Done).
  const k = d.categories.find((x) => x.id === cat);
  const items = k?.itemized && k.kind !== 'set_aside' ? categoryItems(d, k.id).filter((i) => i.kind !== 'transfer') : [];
  if (items.length) {
    const picked = c.item !== undefined ? c.item : cat === t.categoryId ? t.itemId : null;
    rows.push(
      optRow(
        id,
        'item',
        'Item',
        items.find((i) => i.id === picked)?.name ?? 'None',
        state.open.has('item'),
        `<div class="chips">${[{ id: '', name: 'None' }, ...items]
          .map((i) => `<button class="chip" data-a="new-item-pick" data-id="${esc(id)}" data-item="${esc(i.id)}" aria-pressed="${(picked ?? '') === i.id}">${esc(i.name)}</button>`)
          .join('')}</div>`,
      ),
    );
  }
  // Tip: a switch; on, 15 / 18 / 20%, a tip or the final total, and "Total $X". Off: no tip.
  if (canTip(d, t)) {
    const tip = c.tip && !c.tip.off ? c.tip : { tipCents: t.tipCents, tipped: t.usuallyTipped };
    rows.push(switchRow(id, tipProminent(d, t, cat) && !tipOn(t, c) ? TIP_ADD_PROMPT : 'Tip', 'data-new-tip-on', tipOn(t, c), newTipBlock(d, t, tip)));
  }
  // The split: a switch; on, the percent picker. It shares the tipped total and sends the note below.
  rows.push(newSplitBlock(d, t, c.split, rowTotal(t, c.tip)));
  // Paid from savings: a switch; on, the funds to choose from (none picked by itself). A
  // remembered fund is a one-tap offer under the switch while it is off.
  if (canFund(d, t)) {
    const on = fundOn(t, c);
    const picked = fundPicked(t, c);
    const usual = !on && t.usualFundId ? funds(d).find((k) => k.id === t.usualFundId) : undefined;
    const again = usual ? `<button class="linkbtn fundagain" data-a="new-fund-pick" data-id="${esc(id)}" data-fund="${esc(usual.id)}">${esc(fundAgain(usual.name))}</button>` : '';
    const name = funds(d).find((k) => k.id === picked)?.name;
    const list = `<div class="chips">${funds(d)
      .map((k) => `<button class="chip" data-a="new-fund-pick" data-id="${esc(id)}" data-fund="${esc(k.id)}" aria-pressed="${k.id === picked}">${esc(k.name)}</button>`)
      .join('')}</div>${name ? `<p class="hint fundline">${esc(fundPaidLine(name))}</p>` : ''}`;
    rows.push(switchRow(id, FUND_LABEL, 'data-new-fund-on', on, list, again));
  }
  return `<div class="grab"></div><h2>${esc(t.merchant)}</h2>
  ${
    canEditAmount(t0)
      ? sheetAmount(t0) + amountField(t0, 'ns-amt', c.amount !== undefined ? (c.amount ?? null) : t0.amountCents, ` data-new-amt="${esc(id)}"`)
      : `<p class="sheetamt"><b>${money(t.countedCents, 2)}</b></p>`
  }
  <div class="nopts">${rows.join('')}</div>
  ${noteField('ns-note', rowNote(t, c), ` data-new-note="${esc(id)}"`, canSplitNew(d, t) ? { partner: d.partner.linked!.name, on: !!c.split } : undefined)}
  <div class="btns nsheetbtns">${fundMissing(t, c) ? `<p class="hint">${CHOOSE_FUND}.</p>` : ''}<button class="btn primary" data-a="${action}" data-id="${esc(id)}" data-from="sheet"${cat && !fundMissing(t, c) ? '' : ' disabled'}>${confirmLabel(c)}</button><button class="btn" data-a="new-sheet-done">Done</button>${deleteButton(t0.id, 'sheet')}</div>`;
}
